import Button from '@mui/material/Button'
import { useEffect, useRef } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { useTemplateNodeSoftLimit } from 'src/features/templateEdit/useTemplateNodeSoftLimit'

/**
 * 残り容量が少なくなった水準を下回った瞬間に、右上のアラートで知らせる。
 */
export const useTemplateNodeCapacityAlert = () => {
  const { count, limit, isOverLimit, isReady } = useTemplateNodeSoftLimit()
  const { showErrorSnackbar, hideSnackbar } = useSnackbar()
  const previousRemaining = useRef<number | undefined>(undefined)
  const remaining = limit - count

  useEffect(() => {
    if (!isReady) {
      return
    }
    const previous = previousRemaining.current
    previousRemaining.current = remaining

    // ひな形を開いた時点の残り容量は、下回った瞬間ではないので知らせない
    if (previous === undefined) {
      return
    }
    if (isOverLimit) {
      return
    }
    // 実際の残り容量ではなく、下回った水準の数で知らせる
    const crossedThreshold = findCrossedRemainingCapacityThreshold(
      previous,
      remaining,
    )
    if (crossedThreshold === undefined) {
      return
    }

    showErrorSnackbar(`利用可能な容量が残り ${crossedThreshold} 以下です`, {
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      action: (
        <Button color="inherit" size="small" onClick={hideSnackbar}>
          閉じる
        </Button>
      ),
    })
  }, [isReady, isOverLimit, remaining, showErrorSnackbar, hideSnackbar])
}

/** 残り容量がこの数を下回ったら知らせる水準。少ない順。 */
const REMAINING_CAPACITY_THRESHOLDS = [50, 100]

/**
 * 残り容量が新たに下回った水準。下回った瞬間だけ知らせたいので、直前の残り容量と比べて判定する。
 * 1回の編集で複数の水準をまたいだ場合は、より逼迫している方の水準を返す。
 *
 * @returns 下回った水準。下回っていなければ undefined
 */
export const findCrossedRemainingCapacityThreshold = (
  previousRemaining: number,
  remaining: number,
): number | undefined =>
  REMAINING_CAPACITY_THRESHOLDS.find(
    threshold => remaining <= threshold && previousRemaining > threshold,
  )
